/* ─── ImageGalleryManager.js ──────────────────────────────────────────
   • first load = 100 images
   • after "show all" → intersection-scroll 50 each
   • total count fetched once with count_only=1
   • duplicates prevented (Set), share / download limits, action-bar
   ------------------------------------------------------------------ */

   import React, {
    useState, useEffect, useRef, useCallback, memo
  } from "react";
  import { useLocation, useHistory } from "react-router-dom";
  import TopSectionManager from "./TopSectionManager";
  import "./ImageGalleryManager.css";
  import languageMap from "../languageMap";
  import {
    getImages,
    getImagesCount,
    removeImage
  } from "../../services/imageService";
  import JSZip from "jszip";
  import { saveAs } from "file-saver";
  
  /* ---------- constants ---------- */
  const INITIAL_BATCH     = 100;
  const PAGE_SIZE         = 100;
  const LONG_MS           = 500;
  const MAX_DL_AT_ONCE    = 30;
  const MAX_SHARE_AT_ONCE = 5;
  const ZIP_CONC          = 6;
  
  /* ---------- helpers ---------- */
  const fileName = img =>
    (img?.file_name || img?.image_name ||
     (img?.url_origin || img?.url_small || "")
       .split("/").pop()?.split("?")[0] ||
     img?.image_id || "image").toLowerCase();
  
  const isShareCancel = e =>
    e?.name === "AbortError" || e?.name === "NotAllowedError" ||
    (e instanceof DOMException && e.code === 20);
  
  /* ---------- memoised thumbnail ---------- */
  const Item = memo(({ img, idx, marked, onDown, onUp, onLeave }) => (
    <div
      className={`image-item ${marked ? "selected" : ""}`}
      onPointerDown={e => onDown(e, idx)}
      onPointerUp={e => onUp(e, idx)}
      onPointerLeave={onLeave}
    >
      <img src={img.url_small} alt="" />
      {marked && <img src="/icons/v.png" alt="" className="selected-icon" />}
    </div>
  ));
  
  /* ====================================================================
     COMPONENT
     ==================================================================== */
  export default function ImageGalleryManager() {
    /* ---------- routing + meta ---------- */
    const { state } = useLocation();
    const ev        = state?.eventData || {};
    const eventId   = ev.event_id;
    const eventName = ev.event_name;
    const eventDate = ev.event_date;
    const eventLoc  = ev.event_location;
    const guestLink = ev.guest_link || null;   // secure short link
    const history   = useHistory();
    const viewerOpenTs = useRef(0);

    const userId = localStorage.getItem("user_id") || "user_1";
    const lang   = languageMap.config.selectedLanguage || "en";
  
    /* ---------- state ---------- */
    const [imgs,   setImgs]   = useState([]);
    const [page,   setPage]   = useState(0);
    const [hasMore,setHasMore]= useState(true);
    const [loading,setLoading]= useState(true);
    const [total,  setTotal]  = useState(0);
  
    const [sel,  setSel]  = useState([]);
    const [mode, setMode] = useState(false);
    const [showAll, setShowAll] = useState(false);
  
    /* viewer & popup */
    const [viewer, setViewer]         = useState({ open:false, idx:0 });
    const [viewerLoaded, setViewerLoaded] = useState(false);
    const [pop, setPop] = useState({ show:false, text:"", progress:0, zipList:null });
  
    /* ---------- refs ---------- */
    const listRef     = useRef(null);
    const sentinelRef = useRef(null);
    const fileRef     = useRef(null);
    const tRef        = useRef(null);
    const longDoneRef = useRef(false);
    const modeRef     = useRef(false);
    const seenRef     = useRef(new Set());
    useEffect(() => { modeRef.current = mode; }, [mode]);
  
    /* ---------- total count ---------- */
    useEffect(() => {
      if (!eventId) return;
      getImagesCount(eventId).then(setTotal).catch(console.error);
    }, [eventId]);
  
    /* ---------- fetch page ---------- */
    const fetchPage = useCallback(async () => {
      if (!eventId || loading || !hasMore) return;
      if (page > 0 && !showAll) return;
  
      setLoading(true);
      try {
        const isFirst = page === 0;
        const limit   = isFirst ? INITIAL_BATCH : PAGE_SIZE;
        const offset  = isFirst ? 0 : INITIAL_BATCH + (page - 1) * PAGE_SIZE;
        const { images = [] } = await getImages(eventId, limit, offset);
        if (images.length < limit) setHasMore(false);
  
        const unique = images.filter(img => {
          if (seenRef.current.has(img.image_id)) return false;
          seenRef.current.add(img.image_id);
          return true;
        });
        setImgs(prev => [...prev, ...unique]);
        setPage(p => p + 1);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }, [eventId, page, loading, hasMore, showAll]);
  
    /* reset when eventId changes */
    useEffect(() => {
      setImgs([]); setPage(0); setHasMore(true);
      setLoading(true); seenRef.current.clear();
    }, [eventId]);
  
    const fetchInitialPage = async () => {
      setLoading(true);
      try {
        const { images } = await getImages(eventId, INITIAL_BATCH, 0);
        const unique = images.filter(img => {
          if (seenRef.current.has(img.image_id)) return false;
          seenRef.current.add(img.image_id);
          return true;
        });
        setImgs(unique);
        setPage(1);
        if (images.length < INITIAL_BATCH) setHasMore(false);
      } catch (err) {
        console.error("initial load failed", err);
      } finally {
        setLoading(false);
      }
    };
  
    useEffect(() => {
      if (showAll && hasMore) fetchPage();
    }, [showAll]);
  

  const handleEditClick = () => {
  history.push("/update-event", {
    event_id:        eventId,
    event_name:      eventName,
    event_date:      eventDate,
    event_location:  eventLoc,
    event_type:      ev.event_type,
    intro_image_url: ev.intro_image_url,
  });
};


    const onFile = async (e) => {
      const files = [...e.target.files];
      if (!files.length) return;
    
      const BATCH = 10;       // כמה Presigned לכל בקשה
      const PRESIGN_CONC = 4; // כמה בקשות מקבילות ל-URLים
      const UPLOAD_CONC = 40;  // כמה העלאות במקביל
    
      let up = 0, tot = files.length;
      setPop({ show: true, text: `מעלה 0 / ${tot}...`, progress: 0 });
    
      try {
        // שלב 1: חיתוך לקבוצות
        const slices = [];
        for (let i = 0; i < tot; i += BATCH) {
          slices.push(files.slice(i, i + BATCH));
        }
    
        // שלב 2: שליחת בקשות Presign מוגבלות ב-concurrency
        const presignResults = [];
    
        for (let i = 0; i < slices.length; i += PRESIGN_CONC) {
          const batch = slices.slice(i, i + PRESIGN_CONC);
          const responses = await Promise.all(
            batch.map(slice =>
              fetch("https://wwjfkvjxih.execute-api.us-east-1.amazonaws.com/upload-url", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  user_id: userId,
                  event_id: eventId,
                  count: slice.length
                })
              }).then(async r => {
                if (!r.ok) {
                  const err = await r.json().catch(() => ({}));
                  throw new Error(err.message || "presign fail");
                }
                return r.json();
              })
            )
          );
          presignResults.push(...responses);
        }
    
        // שלב 3: מיפוי כתובות לקבצים
        const allUploads = presignResults.flatMap((res, i) =>
          res.uploads.map((upload, j) => ({
            file: slices[i][j],
            url: upload.upload_url
          }))
        );
    
        // שלב 4: העלאה של התמונות
        for (let i = 0; i < allUploads.length; i += UPLOAD_CONC) {
          const current = allUploads.slice(i, i + UPLOAD_CONC);
          await Promise.allSettled(
            current.map(({ file, url }) =>
              fetch(url, {
                method: "PUT",
                headers: { "Content-Type": file.type },
                body: file
              }).then(() => {
                up++;
                const pct = Math.round(up / tot * 100);
                setPop({ show: true, text: `מעלה ${up} / ${tot}...`, progress: pct });
              }).catch(err => {
                console.error(err);
                showPopup("❌ שגיאה בהעלאה", 4000);
              })
            )
          );
        }
    
        showPopup("✔️ כל התמונות הועלו!", 3000, 100);
    
        // שלב 5: רענון הגלריה
        showPopup("✔️ כל התמונות הועלו!", { autoCloseMs: 2000, progress: 100 });

        setTimeout(async () => {
          // רענון תמונות
          const { images: raw = [] } = await getImages(eventId);
          const s = new Set();
          setImgs(raw.filter(i => {
            const k = fileName(i);
            if (s.has(k)) return false;
            s.add(k);
            return true;
          }));

          // רענון כמות תמונות
          const count = await getImagesCount(eventId);
          setTotal(count);
        }, 2000);

    
      } catch (err) {
        console.error(err);
        showPopup("❌ שגיאה כללית", 4000);
      } finally {
        e.target.value = "";
      }
    };
    

    useEffect(() => {
      setImgs([]);
      setPage(0);
      setHasMore(true);
      setLoading(true);
      seenRef.current.clear();
    
      if (eventId) fetchInitialPage();
    }, [eventId]);

    /* IntersectionObserver for infinite scroll */
    useEffect(() => {
      const sentinel = sentinelRef.current;
      const rootEl   = listRef.current;
      if (!sentinel || !rootEl) return;
  
      const obs = new IntersectionObserver(
        entries => {
          if (entries[0].isIntersecting) fetchPage();
        },
        { root: rootEl, threshold: 0.05 }
      );
      obs.observe(sentinel);
      return () => obs.disconnect();
    }, [fetchPage]);
  
    /* ---------- popup helpers ---------- */
    const closePopup = () => setPop({ show:false, text:"", progress:0, zipList:null });
    const showPopup  = (text, { progress=null, autoCloseMs=3000, zipList=null } = {}) => {
      setPop({ show:true, text, progress:progress ?? 0, zipList });
      if (autoCloseMs && !zipList) setTimeout(closePopup, autoCloseMs);
    };
  
    /* ---------- ZIP ---------- */
    const downloadZip = async list => {
      if (!list?.length) return;
      showPopup("מכין קובץ ZIP…", { autoCloseMs:0, progress:0 });
      try {
        const zip = new JSZip();
        let done  = 0;
        for (let i = 0; i < list.length; i += ZIP_CONC) {
          const slice = list.slice(i, i + ZIP_CONC);
          const blobs = await Promise.all(slice.map(async img => {
            const res = await fetch(img.url_origin, { mode:"cors" });
            return res.blob();
          }));
          blobs.forEach((blob, j) => {
            const idx = i + j + 1;
            const ext = (blob.type.split("/")[1] || "jpg").split(";")[0];
            zip.file(`heyi_${idx}.${ext}`, blob);
            done++;
          });
          setPop(p => ({
            ...p,
            text: `אוסף ${done}/${list.length}…`,
            progress: Math.round((done / list.length) * 100)
          }));
        }
        const blob = await zip.generateAsync(
          { type:"blob", streamFiles:true },
          meta => setPop(p => ({ ...p, progress: Math.round(meta.percent) }))
        );
        saveAs(blob, "heyi_images.zip");
        closePopup();
      } catch (err) {
        console.error(err);
        showPopup("❌ שגיאה ביצירת ZIP");
      }
    };
  
    /* ---------- selection helpers ---------- */
    const toggle = useCallback(id => {
      setSel(prev => {
        const next = prev.includes(id)
          ? prev.filter(x => x !== id)
          : [...prev, id];
        setMode(next.length > 0);
        modeRef.current = next.length > 0;
        return next;
      });
    }, []);
  
    const selectAll = () => {
      setSel(prev => {
        const next = prev.length === imgs.length ? [] : imgs.map(i => i.image_id);
        setMode(next.length > 0);
        modeRef.current = next.length > 0;
        return next;
      });
    };
  
    /* ---------- pointer (long-press / click) ---------- */
    const down = (_, idx) => {
      longDoneRef.current = false;
      const id = imgs[idx].image_id;
      tRef.current = setTimeout(() => {
        longDoneRef.current = true;
        toggle(id);
      }, LONG_MS);
    };
    const up = (_, idx) => {
      clearTimeout(tRef.current);
      if (longDoneRef.current) return;
      const id = imgs[idx].image_id;
      if (modeRef.current) toggle(id);
      else {
        viewerOpenTs.current = performance.now();      // ← חדש

        setViewer({ open:true, idx });
        setViewerLoaded(false);
      }
    };
    const leave = () => clearTimeout(tRef.current);
  
    /* ---------- download helpers ---------- */
    const buildLink = useCallback(() => {
      // השתמש בקישור המקוצר שה‑backend שלח, ואם אין – חזור ל־legacy
      if (guestLink) return guestLink;
      return `${window.location.origin}/UploadImages?username=${encodeURIComponent(userId)}`+
             `&event=${encodeURIComponent(eventId)}`+
             `&eventName=${encodeURIComponent(eventName)}`;
    }, [guestLink, userId, eventId, eventName]);

    
    const copyLink = () =>
      navigator.clipboard.writeText(buildLink())
        .then(() => showPopup("הקישור הועתק!"));
  
    const download = (url, name) => {
      const a = document.createElement("a");
      a.href = url; a.download = name;
      document.body.appendChild(a); a.click(); a.remove();
    };
    const downloadOne = img => download(img.url_origin, fileName(img));
  
    /* ---------- DL selected ---------- */
    const dlSel = () => {
      if (!sel.length) return showPopup("לא נבחרו תמונות להורדה.");
      if (sel.length > MAX_DL_AT_ONCE) {
        const list = imgs.filter(i => sel.includes(i.image_id));
        return showPopup(`אפשר להוריד עד ${MAX_DL_AT_ONCE} תמונות.\nלהוריד את כולן כ-ZIP?`, {
          autoCloseMs:0, zipList:list
        });
      }
      sel.forEach((id, i) => {
        const img = imgs.find(x => x.image_id === id);
        if (!img?.url_origin) return;
        setTimeout(() => download(img.url_origin, `image_${i+1}.jpg`), 750 * i);
      });
      showPopup("ההורדה התחילה.");
    };
  
    /* ---------- share ---------- */
    const shareMulti = async () => {
      if (!sel.length) {
        const lnk = buildLink();
        try {
          if (navigator.share) {
            await navigator.share({
              title: eventName || "Heyi Event",
              text: `הצטרפו לאירוע שלי ב-Heyi 👇

              📸 העלו תמונה ברורה שלכם
              🔍 המערכת תזהה אתכם בגלריית האירוע
              🖼️ קבלו את כל התמונות בהן אתם מופיעים
              ⬇️ הורידו או שתפו אותן בקלות!`,
              
              url  : lnk
            });
          } else {
            await navigator.clipboard.writeText(lnk);
            showPopup("הקישור הועתק!");
          }
        } catch (e) {
          if (!isShareCancel(e)) {
            console.error(e);
            await navigator.clipboard.writeText(lnk);
            showPopup("הקישור הועתק!");
          }
        }
        return;
      }
  
      if (sel.length > MAX_SHARE_AT_ONCE)
        return showPopup(`אפשר לשתף עד ${MAX_SHARE_AT_ONCE} תמונות בכל פעם.`);
  
      showPopup("מכין קבצים לשיתוף…", { autoCloseMs:0 });
      try {
        const files = await Promise.all(
          sel.map(async (id, i) => {
            const img = imgs.find(x => x.image_id === id);
            const res = await fetch(img.url_origin, { mode:"cors" });
            let blob  = await res.blob();
            if (!blob.type.startsWith("image/"))
              blob = new Blob([blob], { type:"image/jpeg" });
            const ext = blob.type.split("/")[1];
            return new File([blob], `heyi_${i+1}.${ext}`, { type: blob.type });
          })
        );
        const payload = { title:eventName || "Heyi Event", files };
        if (navigator.canShare?.(payload) && navigator.share) {
          await navigator.share(payload);
          closePopup();
        } else showPopup("הדפדפן לא תומך בשיתוף קבצים.");
      } catch (err) {
        if (!isShareCancel(err)) {
          console.error(err);
          showPopup("❌ שגיאה בשיתוף");
        } else closePopup();
      }
    };
  
    /* ---------- delete ---------- */
    const delSel = async () => {
      if (!sel.length) return showPopup("לא נבחרו תמונות.");
      try {
        for (const id of sel) await removeImage(id);
        setImgs(p => p.filter(i => !sel.includes(i.image_id)));
        setSel([]); setMode(false); modeRef.current = false;
        showPopup("✔️ התמונות נמחקו!");
      } catch (e) {
        console.error(e);
        showPopup("❌ שגיאה במחיקה");
      }
    };
  

    

    /* ---------- derived ---------- */
    const allSel = sel.length === imgs.length && imgs.length;
  
    /* ---------- render ---------- */
    return (
      <div className="main-container">
        <TopSectionManager
  eventName={eventName}
  eventDate={eventDate}
  eventLocation={eventLoc}
  eventId={eventId}
  eventType={ev.event_type}
  introImageUrl={ev.intro_image_url}
  onCopyLink={copyLink}                       // ① pass handler
  onShareLink={(shareMulti)}
/>

  
        {/* header */}
        <div className="middle-section">
          {mode ? (
            <>
              <div className="selected-count">{sel.length} נבחרו</div>
              <button className="select-all-btn" onClick={selectAll}>
                <span className={`circular-checkbox ${allSel ? "checked" : ""}`} />
                {allSel ? "בטל בחירת הכול" : "בחר הכול"}
              </button>
            </>
          ) : imgs.length > 0 && (
            <>
              <div className="gallery-text-images2">
                <div className="gallery-text-images-right">
                  <div className="gallery-text">גלריה</div>
                  <div className="gallery-text-images">{total || imgs.length} תמונות</div>
                </div>
              </div>
              <button className="gallery-toggle-btn" onClick={() => setShowAll(!showAll)}>
                {showAll ? "הצג פחות" : "הצג הכול"}
              </button>
            </>
          )}
        </div>
  
        {/* gallery */}
        <div className={`bottom-section ${showAll ? "expanded" : ""}`} ref={listRef}>
        <div className="gallery-container">
          {loading && imgs.length === 0 ? (
            <div className="loading-message">טוען תמונות…</div>
          ) : imgs.length === 0 ? (
            <div className="no-images-message">התמונות יופיעו כאן לאחר העלאה</div>
          ) : (
            <div className="image-gallery">
              {imgs.map((img, idx) => (
                <Item
                  key={img.image_id}
                  img={img}
                  idx={idx}
                  marked={sel.includes(img.image_id)}
                  onDown={down}
                  onUp={up}
                  onLeave={leave}
                />
              ))}
              <div ref={sentinelRef} style={{ height: 1 }} />
              {loading && <div className="loading-more">טוען…</div>}
            </div>
          )}
        </div>


  
          {/* extra buttons */}
          {/* <div className="additional-buttons">
            <button className="additional-btn" onClick={copyLink}>
              <img src="/icons/downloadlink.png" alt="" /> העתק קישור לאירוע
            </button>
            <button className="additional-btn" onClick={() => showPopup("QR קוד הועתק!")}>
              <img src="/icons/qr.png" alt="" /> העתק ברקוד
            </button>
          </div> */}
        </div>
  
        {/* upload */}

        <input
          ref={fileRef}
          type="file"
          style={{ display:"none" }}
          multiple
          accept="image/*"
          onChange={onFile}
        />
  
        {/* action bar */}
        <div className="action-bar">
          {mode ? (
            <>
              <button className="action-item" onClick={shareMulti}>
                <img src="/icons/share.png" alt="" />
              </button>
              <button className="action-item" onClick={dlSel}>
                <img src="/icons/download.png" alt="" />
              </button>
              <button className="action-item" onClick={delSel}>
                <img src="/icons/delete.png" alt="" />
              </button>
            </>
          ) : (
            <>
            <button className="action-item-event" onClick={handleEditClick}>
              <img src="/icons/edit.png" alt="Edit Event" className="action-icon-event" />
            </button>
            <button className="action-item-circular-button" onClick={() => fileRef.current?.click()}>
              <span className="action-button-text">העלה תמונות</span>
              <img src="/icons/add.png" alt="Upload Images" className="action-icon-event" />
            </button>
            <button className="action-item-event" onClick={() => history.goBack()}>
              <img src="/icons/home.png" alt="Home Icon" className="action-icon-event" />
            </button>
            </>
          )}
        </div>
  
        {/* viewer */}
        {viewer.open && (
          <div className="viewer-overlay" onClick={() => {
      // התעלם מקליק שמגיע ממש מיד אחרי פתיחה
      if (performance.now() - viewerOpenTs.current < 250) return;  // ← חדש
      setViewer(v => ({ ...v, open:false }));
    }}>
            <div className="viewer-inner" onClick={e => e.stopPropagation()}>
              <img
                src={imgs[viewer.idx]?.url_small}
                alt=""
                className="viewer-image"
                onLoad={() => setViewerLoaded(true)}
              />
              <button className="viewer-next"
                onClick={() => setViewer(v => ({ ...v, idx:(v.idx+1)%imgs.length }))}>›</button>
              <button className="viewer-prev"
                onClick={() => setViewer(v => ({ ...v, idx:(v.idx-1+imgs.length)%imgs.length }))}>‹</button>
              <button className="viewer-close"
                onClick={() => setViewer(v => ({ ...v, open:false }))}>×</button>
              <div className="viewer-actions">
                <button onClick={() => downloadOne(imgs[viewer.idx])} disabled={!viewerLoaded}>
                  <img src="/icons/download.png" alt="" /> הורד
                </button>
                <button onClick={shareMulti} disabled={!viewerLoaded}>
                  <img src="/icons/share.png" alt="" /> שתף
                </button>
              </div>
            </div>
          </div>
        )}
  
        {/* popup */}
        {pop.show && (
          <div className="popup-overlay">
            <div className="popup">
              <button className="popup-close-btn" onClick={closePopup}>×</button>
              <div className="popup-content">
                <span className="popupText" style={{ whiteSpace:"pre-line" }}>{pop.text}</span>
                {pop.progress > 0 && pop.progress < 100 && (
                  <div className="progress-container">
                    <div className="progress-bar" style={{ width:`${pop.progress}%` }} />
                    <span className="progress-text">{pop.progress}%</span>
                  </div>
                )}
                {pop.zipList && (
                  <button className="popup-zip-btn" onClick={() => downloadZip(pop.zipList)}>
                    הורד כ-ZIP
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
  